export type ImageValidationConfig = {
  requiredWidth: number;
  requiredHeight: number;
  checkTransparency?: boolean;
};

export const validateImageDimensions = async (
  imageDataUrl: string,
  requiredWidth: number,
  requiredHeight: number
): Promise<{ isValid: boolean; error?: string }> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.width !== requiredWidth || img.height !== requiredHeight) {
        resolve({
          isValid: false,
          error: `Image must be exactly ${requiredWidth}x${requiredHeight} pixels. Current size: ${img.width}x${img.height}`,
        });
        return;
      }
      resolve({ isValid: true });
    };
    img.onerror = () => {
      resolve({
        isValid: false,
        error: 'Failed to load image for validation',
      });
    };
    img.src = imageDataUrl;
  });

export const validateImageTransparency = async (imageDataUrl: string): Promise<{ isValid: boolean; error?: string }> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve({
          isValid: false,
          error: 'Failed to create canvas context for transparency check',
        });
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] < 255) {
          resolve({
            isValid: false,
            error: 'Image cannot have transparent pixels',
          });
          return;
        }
      }

      resolve({ isValid: true });
    };
    img.onerror = () => {
      resolve({
        isValid: false,
        error: 'Failed to load image for transparency validation',
      });
    };
    img.src = imageDataUrl;
  });

export const validateImage = async (
  imageDataUrl: string,
  config: ImageValidationConfig
): Promise<{ isValid: boolean; error?: string }> => {
  const dimensionValidation = await validateImageDimensions(imageDataUrl, config.requiredWidth, config.requiredHeight);

  if (!dimensionValidation.isValid) {
    return dimensionValidation;
  }

  if (config.checkTransparency) {
    const transparencyValidation = await validateImageTransparency(imageDataUrl);
    if (!transparencyValidation.isValid) {
      return transparencyValidation;
    }
  }

  return { isValid: true };
};
